import find from "lodash/find";
import get from "lodash/get";
import round from "lodash/round";
import { formattedTarget } from "./actuals";

/**
 * Build a canonical JWPlayer video modal URL.
 *
 * @param {object} params
 * @param {'exercise-set-video'|'watch-video-list'} params.modalType
 * @param {{ videoId, videoOriginal, reviewedByTheCoach?, uploaderId?, thread?: { _id } }} params.video
 * @param {string|number} params.exerciseToEdit  - exercise _id
 * @param {number} params.setIndex
 * @param {number} params.videoIndex
 * @param {string|number} [params.utcDate]
 * @param {string} [params.uploaderId]   - overrides video.uploaderId
 * @param {string} [params.primary]
 * @param {string} [params.secondary]
 * @param {string} [params.type]
 * @param {string} [params.movement]
 * @param {number} [params.setNumber]
 * @param {string} [params.setTarget]
 * @param {string} [params.setComment]
 */
export function buildVideoModalUrl({
  modalType,
  video,
  exerciseToEdit,
  setIndex,
  videoIndex,
  utcDate,
  uploaderId,
  primary,
  secondary,
  type,
  movement,
  setNumber,
  setTarget,
  setComment,
}) {
  const effectiveUploaderId = uploaderId ?? video?.uploaderId;
  const uploaderParam =
    modalType === "watch-video-list"
      ? `mediaAthleteId=${effectiveUploaderId}`
      : `uploaderId=${effectiveUploaderId}`;

  const optionalParts = [
    primary ? `primary=${encodeURIComponent(primary)}` : "",
    secondary ? `secondary=${encodeURIComponent(secondary)}` : "",
    type ? `type=${encodeURIComponent(type)}` : "",
    movement ? `movement=${encodeURIComponent(movement)}` : "",
    `setNumber=${setNumber != null ? encodeURIComponent(setNumber) : ""}`,
    `setTarget=${setTarget ? encodeURIComponent(setTarget) : ""}`,
    `setComment=${setComment ? encodeURIComponent(setComment) : ""}`,
  ]
    .filter(Boolean)
    .join("&");

  const core = [
    `/actions/refresh?modal[type]=${modalType}`,
    `mediaId=${video?.videoId}`,
    `originalId=${video?.videoOriginal}`,
    uploaderParam,
    `reviewedByTheCoach=${video?.reviewedByTheCoach != null ? video.reviewedByTheCoach : ""}`,
    `exerciseToEdit=${exerciseToEdit}`,
    `setIndex=${setIndex}`,
    `videoIndex=${videoIndex}`,
    `threadId=${video?.thread?._id ?? ""}`,
    `utcDate=${utcDate}`,
  ].join("&");

  return optionalParts ? `${core}&${optionalParts}` : core;
}

/**
 * Find previous and next hrefs in an ordered sequence.
 *
 * @param {{ sequence: Array<{ key: string, href: string }>, currentKey: string }} params
 * @returns {{ prevHref: string|undefined, nextHref: string|undefined }}
 */
export function findVideoNeighbors({ sequence, currentKey }) {
  const idx = sequence.findIndex(({ key }) => key === currentKey);
  if (idx === -1) return { prevHref: undefined, nextHref: undefined };
  return {
    prevHref: idx > 0 ? sequence[idx - 1].href : undefined,
    nextHref: idx < sequence.length - 1 ? sequence[idx + 1].href : undefined,
  };
}

/**
 * Resolve everything a controller needs to render the exercise-set-video
 * modal from a "View Video" link - LogTrainingCard's own href is
 * deliberately minimal (no utcDate/primary/secondary/type/movement/
 * threadId), so this fills those in from `locals.activeExercises` and
 * builds the modal's prev/next sequence (scoped to the clicked video's own
 * day, which is a no-op when the caller's activeExercises is already
 * single-day).
 *
 * Callers must check `redirectParams` first and, when non-null,
 * `return res.refreshWith(redirectParams)` immediately instead of
 * rendering - the controller owns the early return, this helper only
 * computes what to redirect with.
 *
 * @param {object} params
 * @param {object} params.query
 * @param {{ type: string }} params.modal
 * @param {object} params.locals - reads activeExercises/activeUser/fromMetric, writes comments as a side effect
 * @param {Function} params.load
 * @param {string} [params.athlete] - fallback uploaderId when the video has none
 * @returns {Promise<{ redirectParams: object|null, resolvedThreadId: string|undefined, prevHref: string|undefined, nextHref: string|undefined }>}
 */
export async function resolveExerciseSetVideoModal({
  query,
  modal,
  locals,
  load,
  athlete,
}) {
  if (modal.type !== "exercise-set-video") {
    return {
      redirectParams: null,
      resolvedThreadId: query.threadId,
      prevHref: undefined,
      nextHref: undefined,
    };
  }

  let resolvedThreadId = query.threadId;
  let resolvedExercise = null;
  let resolvedVideo = null;

  if (
    query.exerciseToEdit != null &&
    query.setIndex != null &&
    query.videoIndex != null &&
    Array.isArray(locals.activeExercises)
  ) {
    resolvedExercise = find(
      locals.activeExercises,
      (e) => String(e._id) === String(query.exerciseToEdit)
    );
    resolvedVideo = get(
      resolvedExercise,
      ["sets", Number(query.setIndex), "videos", String(query.videoIndex)],
      null
    );

    if (resolvedVideo && !resolvedThreadId) {
      resolvedThreadId = resolvedVideo.threadId || resolvedVideo.thread?._id;
    }
  }

  if (!query.utcDate && resolvedExercise && resolvedVideo) {
    return {
      redirectParams: {
        originalId: resolvedVideo?.videoOriginal,
        mediaId: resolvedVideo?.videoId,
        uploaderId: resolvedVideo?.uploaderId || athlete,
        reviewedByTheCoach: resolvedVideo?.reviewedByTheCoach,
        utcDate: resolvedExercise?.utcDate,
        primary: resolvedExercise?.primary || "",
        secondary: resolvedExercise?.secondary || "",
        type: resolvedExercise?.type || "",
        movement: resolvedExercise?.movement || "",
        threadId: resolvedThreadId || "",
      },
      resolvedThreadId,
      prevHref: undefined,
      nextHref: undefined,
    };
  }

  if (resolvedThreadId) {
    await load("comments", { threadId: resolvedThreadId });
  } else {
    locals.comments = [];
  }

  let prevHref;
  let nextHref;
  if (Array.isArray(locals.activeExercises) && resolvedExercise) {
    const sameDayExercises = locals.activeExercises.filter(
      (e) => e.utcDate === resolvedExercise.utcDate
    );
    const sequence = [];
    for (const exercise of sameDayExercises) {
      const exerciseId = String(exercise._id);
      const primary = exercise.primary ?? "";
      const secondary = exercise.secondary ?? "";
      const type = exercise.type ?? "";
      const movement = exercise.movement ?? "";
      (exercise.sets || []).forEach((set, setIdx) => {
        const videos = set.videos || {};
        Object.keys(videos)
          .map(Number)
          .sort((a, b) => a - b)
          .forEach((vidIdx) => {
            const video = videos[vidIdx];
            if (!video?.videoId && !video?.videoOriginal) return;
            const seqOutcomeObj = set.outcomes?.[vidIdx]?.outcome || {};
            const seqIsPowerlifting =
              exercise.type === "primary" || exercise.type === "secondary";
            const { units, intensityMeasure } = locals.activeUser;
            const seqPrecision = units === "metric" ? 2 : 1;
            const seqUserMetrics = units === "imperial" ? "lb" : "kg";
            const seqRawLoad = seqOutcomeObj.load;
            const seqLoad = seqRawLoad
              ? `${round(
                  locals.fromMetric(seqRawLoad, units),
                  seqPrecision
                )} ${seqUserMetrics}`
              : 0;
            const seqTarget =
              seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                ? formattedTarget(
                    seqOutcomeObj,
                    seqIsPowerlifting,
                    set.percentRepMax || undefined,
                    units,
                    seqLoad,
                    intensityMeasure,
                    intensityMeasure !== "rpe",
                    true
                  ) || undefined
                : undefined;
            const seqSetNumber = setIdx + 1;
            sequence.push({
              key: `${exerciseId}:${setIdx}:${vidIdx}`,
              href: buildVideoModalUrl({
                modalType: "exercise-set-video",
                video,
                exerciseToEdit: exerciseId,
                setIndex: setIdx,
                videoIndex: vidIdx,
                utcDate: exercise.utcDate,
                primary,
                secondary,
                type,
                movement,
                setNumber: seqSetNumber,
                setTarget: seqTarget,
                setComment: set.comments?.[vidIdx] || undefined,
              }),
            });
          });
      });
    }
    const currentKey = `${query.exerciseToEdit}:${Number(
      query.setIndex
    )}:${Number(query.videoIndex)}`;
    ({ prevHref, nextHref } = findVideoNeighbors({ sequence, currentKey }));
  }

  return { redirectParams: null, resolvedThreadId, prevHref, nextHref };
}
